import { AddIcon, AsyncSelect, Button, Form, FormField, ModalDialog, Numeric } from "@rothoblaas/design-system";
import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import Icon from "../../../../components/ui/Icon";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useItemOptions, { getItemOptionLabel, ItemOption, toItemOption } from "../../../../hooks/useItemOptions";
import useI18n from "../../../../hooks/useTranslations";
import { ChemicalAnchor, InsertChemicalAnchorBody } from "../../../../http/concreteResin/types/chemicalAnchors";
import useChemicalAnchorsApi from "../../../../http/concreteResin/useChemicalAnchors";
import AdminItemModal from "../../../items/AdminItemModal";
import styles from "./ChemicalAnchorForm.module.css";

interface ChemicalAnchorFormBody {
  item: ItemOption | null;
  mlQuantity: number | null;
}

interface ChemicalAnchorFormProps {
  chemicalAnchor?: ChemicalAnchor;
  onClose: () => void;
}

// The anchor's item and cartridge volume: its visibility and order are set from the list.
const ChemicalAnchorForm: FC<ChemicalAnchorFormProps> = ({ chemicalAnchor, onClose }) => {
  const i18n = useI18n();
  const resinTranslation = i18n.translation.concreteResinModule;
  const dispatch = useContext(NotificationDispatch);
  const chemicalAnchorsApi = useChemicalAnchorsApi();
  const itemOptions = useItemOptions();
  const [isCreateItemOpen, setIsCreateItemOpen] = useState(false);

  const defaultValues: ChemicalAnchorFormBody = {
    item: chemicalAnchor ? toItemOption(chemicalAnchor.item) : null,
    mlQuantity: chemicalAnchor?.mlQuantity ?? null,
  };

  const onSubmit = () => async (formData: ChemicalAnchorFormBody) => {
    if (!formData.item || formData.mlQuantity == null) return;

    const body: InsertChemicalAnchorBody = {
      item: { id: formData.item.id },
      mlQuantity: formData.mlQuantity,
    };

    try {
      if (chemicalAnchor) {
        await chemicalAnchorsApi.updateChemicalAnchor(chemicalAnchor.id, body);
      } else {
        await chemicalAnchorsApi.insertChemicalAnchor(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={`${chemicalAnchor ? i18n.translation.common.edit : i18n.translation.common.add} ${resinTranslation.chemicalAnchor}`}
    >
      <Form<ChemicalAnchorFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control, setValue }) => (
          <>
            <FormFieldsContainer fullWidth>
              <Controller
                name="item"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                    {({ labelId }) => (
                      <div className={styles.selectWithButton}>
                        <AsyncSelect<ItemOption, false>
                          {...field}
                          {...itemOptions}
                          inputId={labelId}
                          isMulti={false}
                          isDisabled={!!chemicalAnchor}
                          placeholder={`${i18n.translation.common.select}...`}
                          getOptionLabel={getItemOptionLabel}
                          getOptionValue={(option) => option.id}
                          isInvalid={fieldState.invalid}
                          onChange={(option) => field.onChange(option)}
                        />
                        <Button
                          title={i18n.translation.items.createNew}
                          className={styles.button}
                          disabled={!!chemicalAnchor}
                          onClick={() => setIsCreateItemOpen(true)}
                        >
                          <Icon glyph={AddIcon} />
                        </Button>
                      </div>
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="mlQuantity"
                control={control}
                rules={{ required: true, min: 0 }}
                render={({ field, fieldState }) => (
                  <FormField label={resinTranslation.mlQuantity} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        isInvalid={isInvalid}
                        allowNegative={false}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
            </FormFieldsContainer>

            {isCreateItemOpen && (
              <AdminItemModal
                onClose={() => setIsCreateItemOpen(false)}
                onCreateSuccess={(item) => {
                  setValue("item", toItemOption(item), { shouldDirty: true, shouldValidate: true });
                  setIsCreateItemOpen(false);
                }}
              />
            )}
          </>
        )}
      </Form>
    </ModalDialog>
  );
};

export default ChemicalAnchorForm;
